<template>
  <form class="space-y-5" @submit.prevent="submit">
    <div class="space-y-3">
      <template v-if="year == 0 && items">
        <div>
          <l-autocomplete
            v-model="model"
            class="grid gap-1"
            label="School"
            label-class="text-gray-700"
            placeholder="Search school..."
            :items="items"
            autofocus
            required
          />
        </div>
        <div class="flex items-center gap-2">
          <l-checkbox
            v-model="partTime"
            name="part_time"
            label="I am attending school part-time"
          />
        </div>
      </template>

      <icon v-else-if="loading" name="loading" class="text-5xl mx-auto opacity-25 text-center"/>

      <template v-else-if="model && school">
        <template v-for="y in maxYear">
          <template v-if="y === year">
            <div :key="y" class="space-y-3">
              <div class="flex justify-between gap-5 text-lg">
                <div class="font-semibold text-blue dark:text-white">
                  {{ yearLabel }} cost of attendance at {{ school.name }}
                </div>
                <div class="font-semibold text-blue dark:text-white">
                  ${{ annualCost }}
                </div>
              </div>

              <l-input
                v-model="budget[`y${year}_tuition`]"
                label="Tuition & fees"
                prefix="$"
                :name="`y${year}_tuition`"
                min="0"
                type="number"
              />

              <l-input
                v-model="budget[`y${year}_room`]"
                label="Room & board"
                prefix="$"
                :name="`y${year}_room`"
                min="0"
                type="number"
              />

              <l-input
                v-model="budget[`y${year}_books`]"
                label="Books & supplies"
                prefix="$"
                :name="`y${year}_books`"
                min="0"
                type="number"
              />

              <l-input
                v-model="budget[`y${year}_transportation`]"
                label="Transportation"
                prefix="$"
                :name="`y${year}_transportation`"
                min="0"
                type="number"
              />

              <l-input
                v-model="budget[`y${year}_other`]"
                :name="`y${year}_other`"
                label="Other personal expenses"
                prefix="$"
                min="0"
                type="number"
              />

              <l-input
                v-model="scholarship"
                label="Annual scholarship"
                prefix="$"
                name="scholarship"
                min="0"
                type="number"
              />
            </div>

            <div v-if="y < maxYear" :key="y" class="mt-5 space-y-3">
              <l-checkbox
                v-model="autofill"
                name="autofill"
                label="Apply the same costs to the next years"
              />
              <div v-if="autofill" class="grid gap-3 mt-3">
                <l-input
                  v-model="tuitionChange"
                  type="number"
                  name="tuition_change"
                  step="any"
                  suffix="%"
                  label="Annual tuition change"
                >
                  <template #help>
                    <slot name="autofill-help-text"/>
                  </template>
                </l-input>
                <l-input
                  v-model="colChange"
                  type="number"
                  name="col_change"
                  step="any"
                  suffix="%"
                  label="Annual cost of living change"
                />
              </div>
            </div>
          </template>
        </template>
      </template>
    </div>

    <div class="w-full flex justify-end gap-5">
      <l-button
        v-if="(year > 0 && autofill) || year == maxYear"
        type="submit"
        variant="primary"
        :disabled="saving"
      >
        {{ saving? 'Saving' : 'Save and finish' }}
      </l-button>
      <l-button
        v-else
        variant="primary"
        type="submit"
      >
        Save and continue
      </l-button>
    </div>
  </form>
</template>

<script setup>
import LAutocomplete from "@/components/inputs/LAutocomplete.vue"
import LButton from "@/components/buttons/LButton.vue"
import LCheckbox from "@/components/inputs/LCheckbox.vue"
import LInput from "@/components/inputs/LInput.vue"
import route from "ziggy-js"
import { api } from "@/api"
import { computed, onMounted, ref, watch } from "vue"
import { config } from "@/support"

const model = defineModel(Object)

const props = defineProps({
  items: { type: Array },
})

const colChange = ref(config("settings.col.increase") * 100)
const school = ref(null)
const year = ref(0)
const tuitionChange = ref()
const autofill = ref(true)
const saving = ref(false)
const scholarship = ref()
const loading = ref(false)
const budget = ref({})
const partTime = ref(false)

const maxYear = computed(() => (partTime.value ? 5 : 3))

const yearLabel = computed(() => {
  switch (year.value) {
    case 2: return "2nd year"
    case 3: return "3rd year"
    case 4: return "4th year"
    case 5: return "5th year"
  }

  return "1st year"
})

const annualCost = computed(() => {
  let total = 0

  if (year.value) {
    total = (budget.value[`y${year.value}_tuition`] || 0)
      + (budget.value[`y${year.value}_books`] || 0)
      + (budget.value[`y${year.value}_room`] || 0)
      + (budget.value[`y${year.value}_transportation`] || 0)
      + (budget.value[`y${year.value}_other`] || 0)
  }

  return total.toLocaleString()
})

const refreshSchoolDetail = () => {
  if (model.value) {
    loading.value = true
    api.schoolDetail(model.value.slug).then(({ data }) => {
      school.value = data
      scholarship.value = data.anticipated_scholarship
      budget.value = {} // reset

      if (!partTime.value) {
        const tuition_tier = data.tuition_tiers[0]
        tuitionChange.value = tuition_tier.increase
        budget.value.y1_tuition = Math.round((tuition_tier.tuition) * (1 + tuitionChange.value / 100))
      }

      loading.value = false
    })
  }
}

const next = () => {
  year.value = year.value < maxYear.value ? year.value + 1 : year.value
}

watch(() => partTime.value, refreshSchoolDetail)

watch(model, refreshSchoolDetail)

const save = () => {
  saving.value = true
  const tuitionMultiplier = 1 + (tuitionChange.value || 0) / 100
  const colMultiplier = 1 + (colChange.value || 0) / 100

  if (autofill.value) {
    for (let i = year.value ; i <= maxYear.value ; i++) {
      const tuition = Math.round(budget.value[`y${year.value}_tuition`] || 0) * tuitionMultiplier ** (i - 1)
      const books = Math.round(budget.value[`y${year.value}_books`] || 0) * colMultiplier ** (i - 1)
      const room = Math.round(budget.value[`y${year.value}_room`] || 0) * colMultiplier ** (i - 1)
      const transportation = Math.round(budget.value[`y${year.value}_transportation`] || 0) * colMultiplier ** (i - 1)
      const other = Math.round(budget.value[`y${year.value}_other`] || 0) * colMultiplier ** (i - 1)

      budget.value[`y${i}_tuition`] = tuition
      budget.value[`y${i}_books`] = books
      budget.value[`y${i}_room`] = room
      budget.value[`y${i}_transportation`] = transportation
      budget.value[`y${i}_other`] = other
      budget.value[`y${i}_discount`] = scholarship
    }
  }

  for (let i = 1 ; i <= maxYear.value ; i++) {
    budget.value[`y${i}_discount`] = scholarship
  }

  api.budgetCreate({
    ...budget.value,
    part_time: partTime.value,
    school: school.value.id,
  }).then(() => {
    window.location.href = route("budgets.show", school.value.slug)
  })
}

const submit = () => {
  if ((year.value > 0 && autofill.value) || year.value == maxYear.value) {
    save()
  } else {
    next()
  }
}

onMounted(() => {
  if (!props.items?.length && !year.value) {
    year.value = 1
  }

  refreshSchoolDetail()
})
</script>
